import { useState } from 'react'
import styled from 'styled-components'
import Modal from '../modal/Modal'
import useTranslation from '../../hooks/useTranslation'
import { TextControl, Button } from './Core'
import { Icon_Delete } from '../../styles/icons'
import slugify from '../../../../common/utils/string/slugify.js'

const InlineTrigger = styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.75rem;
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 0.375rem;
    background: ${({ theme }) => theme.form.textControl.background};
    cursor: pointer;
    min-height: 2.25rem;
    flex-wrap: wrap;
    &:hover { border-color: ${({ theme }) => theme.form.activeBorder}; }
`

const Chip = styled.span`
    font-size: 0.8rem;
    background: ${({ theme }) => theme.btn.secondary};
    color: ${({ theme }) => theme.btn.font};
    border-radius: 0.75rem;
    padding: 0.125rem 0.625rem;
    white-space: nowrap;
`

const PlaceholderText = styled.span`
    color: ${({ theme }) => theme.font.subColor};
    font-size: 0.9rem;
`

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`

const Th = styled.th`
    text-align: left;
    padding: 0.5rem 0.5rem 0.5rem 0;
    font-size: 0.875rem;
    border-bottom: 1px solid ${({ theme }) => theme.modal.divider};
    color: ${({ theme }) => theme.font.subColor};
`

const Td = styled.td`
    padding: 0.3rem 0.25rem;
    vertical-align: middle;
`

const DeleteBtn = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => theme.btn.danger};
    display: flex;
    align-items: center;
    padding: 0.25rem;
    &:hover { opacity: 0.7; }
`

const AddRowBtn = styled(Button)`
    margin-top: 0.75rem;
    font-size: 0.875rem;
`

const ModalTitleText = styled.h4`
    margin: 0;
    font-size: 1rem;
`

const FooterRow = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
`

const MAX_CHIPS = 5

const ArrayModal = ({ id, value, onChange, modalTitle, addLabel, displayField, columns }) => {
    const [show, setShow] = useState(false)
    const [draft, setDraft] = useState([])
    const { t } = useTranslation()

    const arr = Array.isArray(value) ? value : []

    const openModal = () => {
        setDraft(arr.map(row => ({ ...row })))
        setShow(true)
    }

    const closeModal = () => setShow(false)

    const save = () => {
        onChange({ target: { name: id, value: draft } })
        setShow(false)
    }

    const addRow = () => {
        const blank = {}
        columns.forEach(col => { blank[col.id] = '' })
        setDraft(prev => [...prev, blank])
    }

    const deleteRow = (idx) => setDraft(prev => prev.filter((_, i) => i !== idx))

    const updateCell = (idx, colId, val) => {
        setDraft(prev => prev.map((row, i) => {
            if (i !== idx) return row
            const updated = { ...row, [colId]: val }
            const col = columns.find(c => c.id === colId)
            if (col?.slugTarget) {
                const maxLength = col.slugTarget.maxLength || 5
                const currentTarget = row[col.slugTarget.field] || ''
                const prevSource = row[colId] || ''
                if (!currentTarget || currentTarget === slugify(prevSource, maxLength)) {
                    updated[col.slugTarget.field] = slugify(val, maxLength)
                }
            }
            return updated
        }))
    }

    const chips = arr.map(row => row[displayField]).filter(Boolean)

    return (
        <>
            <InlineTrigger onClick={openModal}>
                {chips.length === 0
                    ? <PlaceholderText>{t(addLabel)}</PlaceholderText>
                    : <>
                        {chips.slice(0, MAX_CHIPS).map((chip, i) => <Chip key={i}>{chip}</Chip>)}
                        {chips.length > MAX_CHIPS && <Chip>+{chips.length - MAX_CHIPS}</Chip>}
                    </>
                }
            </InlineTrigger>

            {show && (
                <Modal show={show} setShow={setShow} onClose={closeModal} minHeight='16rem'>
                    <Modal.Header onClose={closeModal}>
                        <ModalTitleText>{t(modalTitle)}</ModalTitleText>
                    </Modal.Header>
                    <Modal.Body>
                        <Table>
                            <thead>
                                <tr>
                                    {columns.map(col => <Th key={col.id}>{t(col.label)}</Th>)}
                                    <Th style={{ width: '2.5rem' }} />
                                </tr>
                            </thead>
                            <tbody>
                                {draft.map((row, idx) => (
                                    <tr key={idx}>
                                        {columns.map(col => (
                                            <Td key={col.id}>
                                                <TextControl
                                                    type='text'
                                                    value={row[col.id] || ''}
                                                    onChange={e => updateCell(idx, col.id, e.target.value)}
                                                    required={col.required || false}
                                                />
                                            </Td>
                                        ))}
                                        <Td>
                                            <DeleteBtn type='button' onClick={() => deleteRow(idx)}>
                                                {Icon_Delete}
                                            </DeleteBtn>
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        <AddRowBtn type='button' $variant='secondary' onClick={addRow}>
                            + {t(addLabel)}
                        </AddRowBtn>
                    </Modal.Body>
                    <Modal.Footer>
                        <FooterRow>
                            <Button type='button' $variant='secondary' onClick={closeModal}>
                                {t('all.confirm.cancel')}
                            </Button>
                            <Button type='button' $variant='primary' onClick={save}>
                                {t('all.form.button.save')}
                            </Button>
                        </FooterRow>
                    </Modal.Footer>
                </Modal>
            )}
        </>
    )
}

export default ArrayModal
